interface IJobClassification {
  arrayId: string;
  title: string;
  noOfJobs: number;
  jobType: 'Full-time' | 'Part-time';
  employmentDuration: 'Temporary' | 'Permanent';
}

interface IEconomicOverviewSection1 {
  affordableHousing: string;
  createNewJob: string;
  jobClassification: IJobClassification[];
  willResultRhu: string;
  rhuCount?: number;
  ahCount?: number;
}

interface IEconomicOverviewSection2 {
  description1: string;
  description2: string;
  description3: string;
}

interface IEconomicOverviewSection3 {
  q1: string;
  q2: string;
  q3: string;
  q4: string;
  q5: string;
}

interface IEconomicOverviewSection4 {
  advDisadvantagedBusiness: string;
  description: string;
}

interface IEconomicOverviewProps {
  step4: {
    section1: IEconomicOverviewSection1;
    section2: IEconomicOverviewSection2;
    section3: IEconomicOverviewSection3;
    section4: IEconomicOverviewSection4;
  };
}

export default function EconomicOverview({ step4 }: IEconomicOverviewProps) {
  return (
    <section aria-labelledby='applicant-EconomicOverview-title'>
      <div className='bg-white shadow sm:rounded-lg'>
        <div className='py-5'>
          <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
            <h2 id='applicant-EconomicOverview-title' className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'>
              Economic Overview
            </h2>
          </div>
        </div>
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6 flex flex-col gap-3'>
          <div>
            <h2 className='mt-1 max-w-2xl '>Job Creation / Retention</h2>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>Upon completion of project, will new jobs be created?</dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>{step4?.section1?.createNewJob}</dd>
              </div>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>Describe Job Classifications</dt>
                {step4?.section1?.createNewJob === 'yes' ? (
                  <div className='mt-4 w-full ring-1 ring-neutralColor-lighter overflow-hidden'>
                    <table className='divide-y divide-gray-200 w-full'>
                      <thead className='bg-gray-100'>
                        <tr>
                          <th className='p-4 font-bold tracking-wider text-neutralColor-normal text-left text-xs'>Name</th>
                          <th className='p-4 whitespace-nowrap font-bold tracking-wider text-neutralColor-normal text-left text-xs'>
                            How Many
                          </th>
                          <th className='p-4 whitespace-nowrap font-bold tracking-wider text-neutralColor-normal text-left text-xs'>
                            Job Type
                          </th>
                          <th className='p-4 whitespace-nowrap font-bold tracking-wider text-neutralColor-normal text-left text-xs'>
                            Employment Duration
                          </th>
                        </tr>
                      </thead>
                      <tbody className='bg-white divide-y divide-gray-200'>
                        {step4?.section1?.jobClassification.map((finance: IJobClassification) => (
                          <tr key={finance.arrayId} className='w-full p-4'>
                            <td className='p-4 capitalize'>{finance?.title}</td>
                            <td className='p-4 capitalize'>{finance?.noOfJobs}</td>
                            <td className='p-4 capitalize'>{finance?.jobType || '-'}</td>
                            <td className='p-4 capitalize'>{finance?.employmentDuration || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <dd>None</dd>
                )}
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Project result in residential housing units</dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>{step4?.section1?.willResultRhu}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>How Many</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step4?.section1?.rhuCount ?? 'None'}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Will any be affordable housing?</dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>{step4?.section1.affordableHousing}</dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>How Many</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step4?.section1?.ahCount ?? 'None'}</dd>
              </div>
            </dl>
          </div>
          <div>
            <h2 className='max-w-2xl '>Additional Investment</h2>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>Description of private investment committed </dt>
                <dd className='mt-1 text-sm text-gray-900'>{step4?.section2?.description1 ? step4?.section2?.description1 : 'None'}</dd>
              </div>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>Description of the scope of work </dt>
                <dd className='mt-1 text-sm text-gray-900'>{step4?.section2?.description2 ? step4?.section2?.description2 : 'None'}</dd>
              </div>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>Other additional involved investment </dt>
                <dd className='mt-1 text-sm text-gray-900'>{step4?.section2?.description3 ? step4?.section2?.description3 : 'None'}</dd>
              </div>
            </dl>
          </div>
          <div>
            <h2 className='mt-1 max-w-2xl '>Building Location / Condition</h2>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>Is this building previously vacant and being returned use?</dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>{step4?.section3?.q1}</dd>
              </div>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>
                  Is the property severely deteriorated with a negative impact on the community?
                </dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>{step4?.section3?.q2}</dd>
              </div>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>Is the property identified as blighted?</dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>{step4?.section3?.q3}</dd>
              </div>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>The priority development area</dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>{step4?.section3?.q4}</dd>
              </div>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>
                  Is this property in a prominent, highly visible location or located on a major arterial highway?
                </dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>{step4?.section3?.q5}</dd>
              </div>
            </dl>
          </div>
          <div>
            <h2 className='mt-1 max-w-2xl '>Disadvantaged Business</h2>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>
                  Will this project advance disadvantaged business, defined as minority or woman owned business, or small business?
                </dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>{step4?.section4?.advDisadvantagedBusiness}</dd>
              </div>
              <div className='col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>Disadvantaged business details</dt>
                <dd className='mt-1 text-sm text-gray-900'>{step4?.section4?.description ? step4?.section4?.description : 'None'}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}
